import React, { useState } from "react";
import {
  Box,
  Button,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  Typography,
} from "@mui/material";
import AddIcon from "@mui/icons-material/Add";
import FileUploadOutlinedIcon from "@mui/icons-material/FileUploadOutlined";
import { useNavigate } from "react-router-dom";
import { useTranslation } from "react-i18next";
import color from "../../../Constants/colors";
import { Features, UserRole } from "../../../Enums/companyStatus";
import { hasPermission, hasRole } from "../../../Helpers/authHelpers";
import { useVoicesCampaigns } from "../../../Query/useVoicesCampaigns";
import { ComoButton } from "../../Common/ComoButton";
import VoicesCard from "../Common/VoicesCard";
import VoicesCampaignDialog from "./VoicesCampaignDialog";
import VoicesCampaignImportDialog from "./VoicesCampaignImportDialog";
import VoicesCampaignStatusChip from "./VoicesCampaignStatusChip";
import { VoicesCampaignFormat } from "./voicesCampaignFormat";

const outlinedSx = {
  textTransform: "none",
  color: color.gray900,
  borderColor: color.gray300,
  "&:hover": { borderColor: color.gray400, backgroundColor: color.gray50 },
};

const VoicesCampaignList: React.FC = () => {
  const { t } = useTranslation("voicesCampaigns");
  const navigate = useNavigate();
  const { data: campaigns = [], isLoading } = useVoicesCampaigns();
  const canManage = hasPermission(Features.VOICES_MARKETING);
  const seesAll = canManage || hasRole(UserRole.ADMIN);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [importOpen, setImportOpen] = useState(false);

  return (
    <>
      <VoicesCard
        title={t("campaigns")}
        subtitle={seesAll ? t("campaignsSubtitle") : t("campaignsSubtitlePosting")}
        action={
          canManage && (
            <Box sx={{ display: "flex", gap: 1, flexWrap: "wrap" }}>
              <Button
                size="small"
                variant="outlined"
                startIcon={<FileUploadOutlinedIcon fontSize="small" />}
                onClick={() => setImportOpen(true)}
                sx={outlinedSx}
              >
                {t("import")}
              </Button>
              <ComoButton startIcon={<AddIcon />} onClick={() => setDialogOpen(true)}>
                {t("newCampaign")}
              </ComoButton>
            </Box>
          )
        }
      >
        {!isLoading && !campaigns.length ? (
          <Typography variant="body2" sx={{ color: color.gray500 }}>
            {t("noCampaigns")}
          </Typography>
        ) : (
          <Table size="small">
            <TableHead>
              <TableRow>
                <TableCell>{t("name")}</TableCell>
                <TableCell>{t("period")}</TableCell>
                <TableCell>{t("searchTerms")}</TableCell>
                <TableCell>{t("status")}</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {campaigns.map((campaign) => (
                <TableRow
                  key={campaign.id}
                  hover
                  onClick={() => navigate(`/voices/campaigns/${campaign.id}`)}
                  sx={{ cursor: "pointer" }}
                >
                  <TableCell>
                    <Typography variant="body2" sx={{ fontWeight: 500 }}>
                      {campaign.name}
                    </Typography>
                    {campaign.description && (
                      <Typography variant="caption" sx={{ color: color.gray500 }}>
                        {campaign.description}
                      </Typography>
                    )}
                  </TableCell>
                  <TableCell>
                    {VoicesCampaignFormat.period(campaign, t("noPeriod"))}
                  </TableCell>
                  <TableCell>{campaign.search_terms.join(", ") || "–"}</TableCell>
                  <TableCell>
                    <VoicesCampaignStatusChip status={campaign.status} />
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        )}
      </VoicesCard>

      <VoicesCampaignDialog
        open={dialogOpen}
        campaign={null}
        onClose={() => setDialogOpen(false)}
        onSaved={(campaign) => navigate(`/voices/campaigns/${campaign.id}`)}
      />
      <VoicesCampaignImportDialog open={importOpen} onClose={() => setImportOpen(false)} />
    </>
  );
};

export default VoicesCampaignList;
